import React, { useEffect, useState } from 'react';
import { Award, Pencil, ClipboardList, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { useApi } from './DashboardLayout';
import { DashboardBanner } from './Dashboard';
import { Link, useNavigate } from 'react-router-dom';

const DF = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <label className="of-label">{label}</label>
    <div className="of-value">{value || '-'}</div>
  </div>
);

function ProviderBlock({ provider, offers, isOpen, onToggle, navigate }) {
  const certCount = offers.filter(o => o.certified === 1).length;

  return (
    <div className="of-card" style={{ marginBottom: 16 }} data-testid={`managed-provider-${provider.id}`}>
      {/* Header */}
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', cursor: 'pointer',
        borderBottom: isOpen ? '1px solid var(--border)' : 'none',
        background: isOpen ? 'var(--bg-secondary, #f9fafb)' : 'transparent',
        transition: 'background 0.15s', borderRadius: isOpen ? '12px 12px 0 0' : 12,
      }}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{provider.verein || 'Unbekannt'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {provider.city_location || ''} &middot; Club-ID: {provider.club_id || '-'} &middot; {offers.length} Angebot{offers.length !== 1 ? 'e' : ''}{certCount > 0 && `, ${certCount} zertifiziert`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <Link to={`/app/providers/${provider.id}/edit?back=/app`} className="btn btn-secondary btn-sm"
            style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
            data-testid={`edit-provider-${provider.id}`}>
            <Pencil size={12} /> Profil bearbeiten
          </Link>
          <Link to={`/app/offers/new?provider_id=${provider.id}&back=/app`} className="btn btn-primary btn-sm"
            style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
            data-testid={`add-offer-${provider.id}`}>
            <Plus size={12} /> Neues Angebot
          </Link>
        </div>
      </div>

      {isOpen && (
        <div style={{ padding: '16px 20px' }}>
          {/* Contact info */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0 24px', marginBottom: 16 }}>
            <DF label="Verein / Institution" value={provider.verein} />
            <DF label="IK-Nummer" value={provider.ik_number} />
            <DF label="Ansprechpartner" value={provider.contact_person} />
            <DF label="Adresse" value={[provider.street, `${provider.postal_code || ''} ${provider.city_location || ''}`].filter(Boolean).join(', ')} />
            <DF label="Telefon" value={provider.phone ? <a href={`tel:${provider.phone}`} style={{ color: 'var(--primary)' }}>{provider.phone}</a> : '-'} />
            <DF label="E-Mail" value={provider.email ? <a href={`mailto:${provider.email}`} style={{ color: 'var(--primary)' }}>{provider.email}</a> : '-'} />
            {provider.website && <DF label="Website" value={<a href={provider.website} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{provider.website}</a>} />}
            {provider.dachverband && <DF label="Dachverband" value={provider.dachverband} />}
          </div>

          {/* Offers - matching ProviderHomePage table format */}
          <div style={{ marginTop: 8 }}>
            <div className="of-section" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ClipboardList size={14} /> Angebote ({offers.length})
              {certCount > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: '#10B981', marginLeft: 4 }}>– {certCount} zertifiziert</span>}
            </div>
            {offers.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Keine Angebote vorhanden</div>
            ) : (
              <div className="table-wrap" style={{ margin: '0 -20px -20px' }}>
                <table data-testid={`offers-table-${provider.id}`}>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Nr.</th>
                      <th>Angebot</th>
                      <th>Veranstaltungsort</th>
                      <th>Zeit</th>
                      <th>Zertifizierung</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map(o => (
                      <tr key={o.id}
                        onClick={() => navigate(`/app/offers/${o.id}/edit?back=/app`)}
                        style={{ cursor: 'pointer' }}
                        data-testid={`offer-${o.id}`}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{o.id}</td>
                        <td style={{ fontWeight: 500 }}>{o.angebot || '-'}</td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          {[o.veranstaltungsort_name, o.veranstaltungsort_strasse, o.veranstaltungsort_plz, o.veranstaltungsort_ort].filter(Boolean).join(', ') || '-'}
                        </td>
                        <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {o.wochentag ? `${o.wochentag}${o.uhrzeit_von ? ` ${o.uhrzeit_von}` : ''}${o.uhrzeit_bis ? `–${o.uhrzeit_bis}` : ''}` : '-'}
                        </td>
                        <td>
                          {o.deregistration_date ? (
                            <span className="badge" style={{ background: '#FEF3C7', color: '#92400E', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
                              Abmeldung {o.deregistration_date.split('-').reverse().join('.')}
                            </span>
                          ) : o.certified === 1 ? (
                            <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
                              <Award size={10} /> Zertifiziert
                              {o.cert_valid_until && <span style={{ opacity: 0.7, fontSize: 10, marginLeft: 2 }}>bis {o.cert_valid_until?.substring(0, 10)}</span>}
                            </span>
                          ) : (
                            <span className="badge badge-warning" style={{ fontSize: 10 }}>Nicht zertifiziert</span>
                          )}
                        </td>
                        <td>
                          <Pencil size={13} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManagerHomePage({ user }) {
  const { apiFetch } = useApi();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [allOffers, setAllOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProviders, setOpenProviders] = useState({});
  const [banners, setBanners] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [provR, offR, bannerR] = await Promise.all([
      apiFetch('/api/providers/managed'),
      apiFetch('/api/offers'),
      apiFetch('/api/settings/info-banner'),
    ]);
    const pd = await provR?.json();
    if (Array.isArray(pd)) {
      setProviders(pd);
      const open = {};
      pd.forEach(p => { open[p.id] = true; });
      setOpenProviders(open);
    }
    const od = await offR?.json();
    if (Array.isArray(od)) setAllOffers(od);
    try { const bd = await bannerR?.json(); if (bd?.banners) setBanners(bd.banners.filter(b => b.enabled_provider && b.content)); } catch {}
    setLoading(false);
  };

  const toggleProvider = (id) => {
    setOpenProviders(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  return (
    <div data-testid="manager-home-page">
      <div className="page-header">
        <h2>Mein Bereich</h2>
        <p>{user?.first_name || ''} {user?.last_name || ''} &middot; {providers.length} Standort{providers.length !== 1 ? 'e' : ''}</p>
      </div>

      {banners.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: banners.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {banners.map(b => <DashboardBanner key={b.id} banner={b} />)}
        </div>
      )}

      {providers.length === 0 ? (
        <div className="of-card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <p>Ihnen sind noch keine Anbieter/Standorte zugewiesen.</p>
          <p style={{ fontSize: 12 }}>Bitte wenden Sie sich an den Administrator.</p>
        </div>
      ) : (
        providers.map(p => (
          <ProviderBlock
            key={p.id}
            provider={p}
            offers={allOffers.filter(o => o.provider_id === p.id)}
            isOpen={!!openProviders[p.id]}
            onToggle={() => toggleProvider(p.id)}
            navigate={navigate}
          />
        ))
      )}
    </div>
  );
}
